import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button, ButtonProps } from '../../Shared/Button/Button';
import { ChangePlanDialog } from '../ChangePlanDialog/ChangePlan.dialog';
import { DowngradeToTierPlanDialog } from '../DowngradeToTierPlanDialog/DowngradeToTierPlanDialog';
import {
  SubscriptionPlanConfigDto,
  SubscriptionPlansDto,
} from '@/Domain/api/Subscription/dto/SubscriptionPlans.dto';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useDraftSubscription } from '@/Domain/hooks/Subscription/useDraftSubscription.hook';
import { useUpdateSubscription } from '@/Domain/hooks/Subscription/useUpdateSubscription.hook';
import { StripeData } from '@/UI/Subscription/Subscription.page';

type Props = {
  tier: TierEnum;
  isBillingYearly: boolean;
  stripeData: StripeData;
  label: string;
  variant: ButtonProps['variant'];
  disabled: boolean;
};

function getStripePlan(
  tier: TierEnum,
  plans: SubscriptionPlansDto['products'],
): SubscriptionPlanConfigDto {
  const emptyStripePlan = {
    price_annually: '$0',
    price_monthly: '$0',
  };
  switch (tier) {
    case TierEnum.STARTER:
      return plans.starter ?? emptyStripePlan;
    case TierEnum.BASIC_2026:
      return plans.basic_2026 ?? emptyStripePlan;
    case TierEnum.ESSENTIAL_2026:
      return plans.essential_2026 ?? emptyStripePlan;
    case TierEnum.PROFESSIONAL_2026:
      return plans.professional_2026 ?? emptyStripePlan;
    case TierEnum.BUSINESS_2026:
      return plans.business_2026 ?? emptyStripePlan;
    default:
      return emptyStripePlan;
  }
}

export const ChoosePlanButton = ({
  tier,
  isBillingYearly,
  stripeData,
  label,
  variant,
  disabled,
}: Props) => {
  const { currentPlan, subscription, plans } = stripeData;

  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);
  const [isChangeToTierPlanDialogOpen, setIsChangeToTierPlanDialogOpen] = useState(false);

  const interval = useMemo(
    () => (isBillingYearly ? SubscriptionIntervalEnum.Annually : SubscriptionIntervalEnum.Monthly),
    [isBillingYearly],
  );

  const updateSubscription = useUpdateSubscription({
    successCallback: () => setIsChangePlanDialogOpen(false),
    fromTier: currentPlan.tier,
    toTier: tier,
    interval,
  });

  const draftSubscription = useDraftSubscription();

  const priceId = isBillingYearly
    ? getStripePlan(tier, plans)?.price_annually
    : getStripePlan(tier, plans)?.price_monthly;

  const handleSelectPlan = () => {
    if (SubscriptionHelper.isFreeAccount(currentPlan.tier)) {
      return updateSubscription.handleUpdate(priceId);
    }

    setIsChangePlanDialogOpen(true);
    draftSubscription.handleDraft(priceId);
    return;
  };

  const handleSelectTierPlan = () => setIsChangeToTierPlanDialogOpen(true);

  const handleClick = () => {
    switch (tier) {
      case TierEnum.FREE:
        return handleSelectTierPlan();
      case TierEnum.CUSTOM:
        return window.open('https://www.quickchat.ai/contact');
      default:
        return handleSelectPlan();
    }
  };

  return (
    <>
      <Button
        variant={variant}
        className="w-full"
        onClick={handleClick}
        disabled={disabled || updateSubscription.isPending}
      >
        {updateSubscription.isPending ? <Loader2 className="animate-spin" /> : label}
      </Button>

      <DowngradeToTierPlanDialog
        currentPlan={currentPlan}
        canceled={subscription.canceled}
        isOpen={isChangeToTierPlanDialogOpen}
        onOpenChange={() => setIsChangeToTierPlanDialogOpen((prev) => !prev)}
      />

      <ChangePlanDialog
        interval={interval}
        nextTier={tier}
        currentPlan={currentPlan}
        canceled={subscription.canceled}
        data={draftSubscription.data}
        isOpen={isChangePlanDialogOpen}
        isPending={draftSubscription.isPending || updateSubscription.isPending}
        onOpenChange={() => {
          setIsChangePlanDialogOpen((prev) => !prev);
          draftSubscription.handleCancel();
        }}
        onCallback={() => updateSubscription.handleUpdate(priceId)}
      />
    </>
  );
};
